<template>
  <div
    class="t-t-slider flex w-full items-center"
    :class="{
      'gap-4': !xxs,
      'gap-3.5': xxs,
    }"
  >
    <div class="min-w-0 flex-1">
      <vue-slider
        v-model="localVal"
        :min="min"
        :max="max"
        :height="8"
        :dot-size="xxs ? 22 : 24"
        :duration="0.3"
        @drag-start="$emit('drag-start')"
        @drag-end="$emit('drag-end')"
      ></vue-slider>
    </div>
    <t-input-number
      v-model="localVal"
      :controls="false"
      :min="min"
      :max="max"
      :unit="unit"
      :width="25"
      :changed="changed"
      :reset-tooltip="resetTooltip"
      @reset="emit('reset')"
    ></t-input-number>
  </div>
</template>
<script lang="ts" setup>
import VueSlider from "vue-slider-component";
import TInputNumber from "../TInputNumber/TInputNumber.vue";
import "vue-slider-component/lib/theme/default.scss";
import { computed } from "vue";

const props = defineProps({
  modelValue: {
    type: Number,
    required: true,
  },
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  unit: {
    type: String,
    default: "%",
  },
  xs: {
    type: Boolean,
    default: false,
  },
  xxs: {
    type: Boolean,
    default: false,
  },
  changed: {
    type: Boolean,
    default: false,
  },
  resetTooltip: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits(["update:modelValue", "drag-start", "drag-end", "reset"]);

const localVal = computed({
  get() {
    if (props.modelValue > props.max) {
      return props.max;
    }
    return props.modelValue;
  },

  set(val: number) {
    emit("update:modelValue", val);
  },
});

const dotSize = computed(() => {
  if (props.xxs) {
    return "22px";
  } else {
    return "24px";
  }
});
</script>
<style lang="scss">
.t-t-slider {
  $themeColor: var(--c-accent-1);
  $disabledOpacity: 0.5 !default;

  $railBorderRadius: 4px !default;

  $dotShadow: none;
  $dotShadowFocus: none;
  $dotBgColor: #fff !default;
  $dotBgColorDisable: #ccc !default;
  $dotBorderRadius: 50% !default;

  $stepBorderRadius: 50% !default;
  $stepBgColor: rgba(0, 0, 0, 0.16) !default;

  $labelFontSize: 14px !default;

  .vue-slider {
    width: 100% !important;
  }

  /* component style */
  .vue-slider-disabled {
    opacity: $disabledOpacity;
    cursor: not-allowed;
  }

  /* rail style */
  .vue-slider-rail {
    background-color: var(--c-g400);
    border-radius: $railBorderRadius;
  }

  /* process style */
  .vue-slider-process {
    background-color: $themeColor;
    border-radius: $railBorderRadius;
  }

  /* mark style */
  .vue-slider-mark {
    z-index: 4;

    &:first-child,
    &:last-child {
      .vue-slider-mark-step {
        display: none;
      }
    }

    @at-root &-step {
      width: 100%;
      height: 100%;
      border-radius: $stepBorderRadius;
      background-color: $stepBgColor;
    }

    @at-root &-label {
      font-size: $labelFontSize;
      white-space: nowrap;
    }
  }

  /* dot style */
  .vue-slider-dot {
    @at-root &-handle {
      cursor: pointer;
      width: v-bind(dotSize);
      height: v-bind(dotSize);
      border-radius: $dotBorderRadius;
      background-color: $dotBgColor;
      box-sizing: border-box;
      border: 4px solid var(--c-accent-1);
      box-shadow: $dotShadow;

      @at-root &-focus {
        box-shadow: $dotShadowFocus;
      }
      @at-root &-disabled {
        cursor: not-allowed;
        background-color: $dotBgColorDisable;
      }
    }

    @at-root &-tooltip {
      @at-root &-inner {
        display: none;
      }
    }

    @at-root &-tooltip-wrapper {
      display: none;
    }
  }
}
</style>
